import { Contractor, Homeowner } from "./data";
import { ImageWithFallback } from "components/ui/ImageWithFallback";

const relibuildIcon = "/relibuild-favicon.ico";

interface ConfirmationScreenProps {
  contractors: Contractor[];
  homeowners: Homeowner[];
  contractorIds: string[];
  homeownerIds: string[];
  submitting?: boolean;
  onConfirm: () => void;
  onBack: () => void;
}

export function ConfirmationScreen({
  contractors,
  homeowners,
  contractorIds,
  homeownerIds,
  submitting = false,
  onConfirm,
  onBack,
}: ConfirmationScreenProps) {
  const selectedContractors = contractors.filter((c) =>
    contractorIds.includes(c.id)
  );
  const selectedHomeowners = homeowners.filter((h) =>
    homeownerIds.includes(h.id)
  );

  const totalSuggestions = contractorIds.length * homeownerIds.length;

  return (
    <div className="">
      <div className="mb-8">
      <div className="mb-2 font-[500] text-[15px]">Confirm Suggestions</div>
      <div className="text-gray-600">
          Review selections before sending
        </div>
      </div>

      <div className="mb-8 rounded-lg border border-primary/20 bg-gray-50 px-6 py-3">
        <div className="text-foreground">
          You are about to send <strong>{contractorIds.length}</strong> contractor
          {contractorIds.length !== 1 ? "s" : ""} to{" "}
          <strong>{homeownerIds.length}</strong> homeowner
          {homeownerIds.length !== 1 ? "s" : ""} ({totalSuggestions} total
          suggestion{totalSuggestions !== 1 ? "s" : ""})
        </div>
      </div>

      <div className="mb-8">
        <div className="mb-4 text-foreground font-[500] text-[15px]">
          Selected Contractors ({contractorIds.length})
        </div>
        <div className="space-y-3">
          {selectedContractors.map((contractor) => (
            <div
              key={contractor.id}
              className="flex items-start gap-4 rounded-lg border border-border bg-background p-4"
            >
              <div className="flex h-12 w-12 flex-none items-center justify-center overflow-hidden rounded-lg bg-white p-2 ring-1 ring-gray-200">
                <ImageWithFallback
                  src={contractor.logo || relibuildIcon}
                  fallbackSrc={relibuildIcon}
                  alt={`${contractor.name} logo`}
                  className="h-full w-full object-contain"
                />
              </div>
              <div className="flex-1">
                <div className="mb-1 text-foreground">{contractor.name}</div>
                <div className="mb-2 text-muted-foreground">{contractor.location}</div>
                <div className="flex flex-wrap gap-2">
             
             {contractor.profile &&  <span
                      key={contractor.profile}
                      className="rounded bg-gray-200 px-2 py-1 text-xs text-foreground"
                    >
                      {contractor.profile}
                    </span>}  
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mb-8">
      <div className="mb-4 text-foreground font-[500] text-[15px]">
          Selected Homeowners ({homeownerIds.length})
        </div>
        <div className="space-y-3">
          {selectedHomeowners.map((homeowner) => (
            <div
              key={homeowner.id}
              className="rounded-lg border border-border bg-background p-4"
            >
              <div className="mb-1 text-foreground">{homeowner.name}</div>
              <div className="mb-1 text-foreground/80">
                {homeowner.address || [homeowner.city, homeowner.state].filter(Boolean).join(", ") || "-"}
              </div>
              <div className="flex items-center gap-4 text-muted-foreground">
                <span>{homeowner.email}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-3 mb-3">
        <button
          type="button"
          onClick={onBack}
          className="rounded-lg border border-border bg-background px-6 py-2 text-foreground hover:bg-accent"
        >
          Back
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={submitting}
          className="contractor-primary-action rounded-lg border px-8 py-2"
        >
          {submitting ? "Sending..." : "Send Suggestions"}
        </button>
      </div>
    </div>
  );
}
